import {reactive} from 'vue'
import {getCompanySubscription} from "@/api/subscription";
import {user} from "@/data/user";

export const subscription = reactive({
    subscription_info: {},
    company_id: '',
    last_update: null,
    cache_ttl: 5 * 60 * 1000, // 5 минут кеш

    /**
     * Получить информацию о подписке для активной компании
     * @param {boolean} force - принудительно обновить данные
     * @returns {Promise<Object>}
     */
    async getSubscriptionInfo(force = false) {
        const active_company = await user.getActiveCompany()
        
        // Если данные уже загружены для этой компании и кеш актуален, возвращаем их
        if (!force && 
            this.company_id === active_company && 
            this.subscription_info.has_active_subscription !== undefined &&
            this.last_update && 
            (Date.now() - this.last_update) < this.cache_ttl) {
            return this.subscription_info
        }

        // Если компания изменилась, сбрасываем данные
        if (this.company_id !== active_company) {
            this.subscription_info = {}
            this.company_id = active_company
        }

        try {
            const result = await getCompanySubscription(active_company)
            this.subscription_info = result.data || {}
            this.company_id = active_company
            this.last_update = Date.now()
        } catch (error) {
            console.error('Ошибка загрузки подписки:', error)
            this.subscription_info = {}
        }

        return this.subscription_info
    },

    /**
     * Проверить, есть ли активная подписка
     * @returns {Promise<boolean>}
     */
    async hasActiveSubscription() {
        const info = await this.getSubscriptionInfo()
        return info.has_active_subscription === true
    },

    /**
     * Проверить, является ли тариф бесплатным
     * @returns {Promise<boolean>}
     */
    async isFree() {
        const info = await this.getSubscriptionInfo()
        if (!info.has_active_subscription || !info.subscription) {
            return false
        }
        const price = info.subscription.price
        return price === 0 || price === null || price === undefined
    },

    /**
     * Проверить, оплачена ли подписка
     * @returns {Promise<boolean>}
     */
    async isPaid() {
        const info = await this.getSubscriptionInfo()
        if (!info.has_active_subscription || !info.subscription) {
            return false
        }
        return info.subscription.is_paid === true
    },

    /**
     * Проверить, не истекла ли подписка
     * @returns {Promise<boolean>}
     */
    async isNotExpired() {
        const info = await this.getSubscriptionInfo()
        if (!info.has_active_subscription || !info.subscription) {
            return false
        }
        
        // Бесплатный тариф всегда активен
        if (await this.isFree()) {
            return true
        }
        
        // Если нет даты окончания, считаем что не истекла
        if (!info.subscription.end_date) {
            return true
        }

        const endDate = new Date(info.subscription.end_date)
        const now = new Date()
        return endDate > now
    },

    /**
     * Проверить, активна ли подписка (не истекла по дате)
     * @returns {Promise<boolean>}
     */
    async isActive() {
        return await this.isNotExpired()
    },

    /**
     * Проверить, скоро ли истекает подписка (в течение 3 дней)
     * @returns {Promise<boolean>}
     */
    async isExpiringSoon() {
        const info = await this.getSubscriptionInfo()
        if (!info.has_active_subscription || !info.subscription) {
            return false
        }
        
        // Бесплатный тариф никогда не истекает скоро
        if (await this.isFree()) {
            return false
        }
        
        // Если нет даты окончания, считаем что не истекает скоро
        if (!info.subscription.end_date) {
            return false
        }

        const endDate = new Date(info.subscription.end_date)
        const now = new Date()
        const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
        
        // Проверяем, что подписка еще активна и до окончания осталось 3 дня или меньше
        return endDate > now && daysUntilExpiry <= 3
    },

    /**
     * Получить лимиты тарифа
     * @param {string} limitType - тип лимита (product, store, user)
     * @returns {Promise<number|null>}
     */
    async getLimit(limitType) {
        const info = await this.getSubscriptionInfo()
        if (!info.total_limits) {
            return null
        }
        return info.total_limits[limitType] || null
    },

    /**
     * Проверить, не превышен ли лимит
     * @param {string} limitType - тип лимита (product, store, user)
     * @param {number} currentCount - текущее количество
     * @returns {Promise<boolean>}
     */
    async checkLimit(limitType, currentCount) {
        const limit = await this.getLimit(limitType)
        if (limit === null) {
            return true // Если лимит не установлен, разрешаем
        }
        return currentCount < limit
    },

    /**
     * Сбросить кеш подписки (например, после оплаты)
     */
    reset() {
        this.subscription_info = {}
        this.company_id = ''
        this.last_update = null
    },

    /**
     * Обновить данные о подписке (принудительно)
     */
    async refresh() {
        return await this.getSubscriptionInfo(true)
    }
})

